/**
 * lots.ts — polygon machinery for BLOCK SUBDIVISION (W2d-3 Task 2).
 *
 * Pure chart-space geometry, no hashing and no sampler: the ORCHESTRATION
 * (face → alley → lots → water/grade/node disciplines → Parcel) lives in
 * growth.ts (emitBlockLots); this module supplies the deterministic polygon
 * operations it composes:
 *
 *   - {@link polygonArea} / {@link pointInPolygon} (crossing number — concave-
 *     safe, unlike the convex-only orientation loop the strip emitter uses);
 *   - {@link convexHull} + {@link obbOf} (rotating calipers) — the split axis
 *     and the aspect-ratio estimate;
 *   - {@link splitPolygonByLine} — cut a SIMPLE (possibly concave) polygon
 *     with a line in general position into its pieces on both sides, via the
 *     arc + sorted-crossing-bridge stitch (crossings along the line alternate
 *     inside/outside for a simple polygon, so bridges pair (c0,c1),(c2,c3),…);
 *   - {@link subdividePolygon} — the recursive lot splitter: cut perpendicular
 *     to the OBB's long axis at mid ± skew jitter (the caller supplies each
 *     cut's jitter through `draw(path)`, keyed on the recursion path so the
 *     tree is a pure function of (face, salt stream)); recurse while a piece
 *     is bigger than `maxLotArea` or skinnier than `aspectMax`; drop pieces
 *     below `minLotArea`. General position is FORCED by deterministic nudge
 *     retries when a cut grazes a vertex (a graze breaks the alternation
 *     pairing, so it is detected and the cut re-offset — never accepted).
 *   - {@link clipHalfPlane} (Sutherland–Hodgman) — the per-lot frontage
 *     SETBACK clip. S–H on a concave subject can in principle emit bridge
 *     artifacts, but a lot is small and near-convex relative to its LOCAL
 *     frontage edge, so the caller's post-clip area gate is the guard.
 *   - {@link sharedBoundaryLength} — the lot-fronts-a-street test.
 *
 * SHARED-CODE DISCIPLINE: pmath only where non-exactly-specified math is
 * needed (atan2/hypot); Math.sqrt/abs/min/max are spec-exact and allowed.
 * The pmath-ban gate covers this file.
 */
import * as pmath from "../pmath.js";

/** Shoelace signed area of [x0,y0, x1,y1, …] (CCW positive). */
export function polygonArea(poly: ArrayLike<number>): number {
  const n = poly.length / 2;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    s += poly[2 * i] * poly[2 * j + 1] - poly[2 * j] * poly[2 * i + 1];
  }
  return s * 0.5;
}

/** Point-in-polygon via crossing number — valid for concave simple polygons. */
export function pointInPolygon(poly: ArrayLike<number>, px: number, py: number): boolean {
  const n = poly.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[2 * i], yi = poly[2 * i + 1];
    const xj = poly[2 * j], yj = poly[2 * j + 1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Strict containment for simple polygons: every vertex is inside and no boundary
 * segment touches or crosses the other boundary. Unlike corner/midpoint probes,
 * this catches a narrow concave notch anywhere along an edge. Touching is rejected
 * deliberately: building footprints require a setback. Either winding is valid.
 */
export function polygonStrictlyContains(outer: ArrayLike<number>, inner: ArrayLike<number>): boolean {
  for (const poly of [outer, inner]) {
    if (poly.length < 6 || poly.length % 2 !== 0) return false;
    for (let i = 0; i < poly.length; i++) if (!Number.isFinite(poly[i])) return false;
    if (Math.abs(polygonArea(poly)) < 1e-9) return false;
  }
  const orient = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number =>
    (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  for (let i = 0; i < inner.length; i += 2) {
    const ni = (i + 2) % inner.length;
    const ax = inner[i], ay = inner[i + 1], bx = inner[ni], by = inner[ni + 1];
    if (!pointInPolygon(outer, ax, ay)) return false;
    for (let j = 0; j < outer.length; j += 2) {
      const nj = (j + 2) % outer.length;
      const cx = outer[j], cy = outer[j + 1], dx = outer[nj], dy = outer[nj + 1];
      if (Math.max(ax, bx) < Math.min(cx, dx) || Math.max(cx, dx) < Math.min(ax, bx)
        || Math.max(ay, by) < Math.min(cy, dy) || Math.max(cy, dy) < Math.min(ay, by)) continue;
      const a = orient(ax, ay, bx, by, cx, cy), b = orient(ax, ay, bx, by, dx, dy);
      const c = orient(cx, cy, dx, dy, ax, ay), d = orient(cx, cy, dx, dy, bx, by);
      const straddles = (x: number, y: number): boolean =>
        (x <= 1e-9 && y >= -1e-9) || (y <= 1e-9 && x >= -1e-9);
      if (straddles(a, b) && straddles(c, d)) return false;
    }
  }
  return true;
}

/** Polygon centroid (vertex mean — the probe point every gate uses). */
export function polygonCentroid(poly: ArrayLike<number>, out: [number, number]): void {
  const n = poly.length / 2;
  let su = 0, sv = 0;
  for (let i = 0; i < n; i++) { su += poly[2 * i]; sv += poly[2 * i + 1]; }
  out[0] = su / n;
  out[1] = sv / n;
}

/** Convex hull (Andrew monotone chain), returned as [x,y,…] CCW. */
export function convexHull(poly: ArrayLike<number>): number[] {
  const n = poly.length / 2;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) pts.push([poly[2 * i], poly[2 * i + 1]]);
  pts.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: [number, number], a: [number, number], b: [number, number]): number =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: [number, number][] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop(); upper.pop();
  const hull = lower.concat(upper);
  const out: number[] = [];
  for (const p of hull) out.push(p[0], p[1]);
  return out;
}

export interface Obb {
  /** Long-axis unit direction. */ ax: number; ay: number;
  /** Extent along the long axis (m). */ long: number;
  /** Extent across it (m). */ short: number;
  /** Centre. */ cx: number; cy: number;
}

/** Minimum-area oriented bounding box of a polygon (rotating calipers on its hull). */
export function obbOf(poly: ArrayLike<number>): Obb {
  const hull = convexHull(poly);
  const n = hull.length / 2;
  if (n < 2) return { ax: 1, ay: 0, long: 0, short: 0, cx: hull[0] ?? 0, cy: hull[1] ?? 0 };
  let best: Obb | null = null;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    let dx = hull[2 * j] - hull[2 * i];
    let dy = hull[2 * j + 1] - hull[2 * i + 1];
    const l = pmath.hypot(dx, dy);
    if (l < 1e-9) continue;
    dx /= l; dy /= l;
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for (let k = 0; k < n; k++) {
      const a = hull[2 * k] * dx + hull[2 * k + 1] * dy;
      const b = -hull[2 * k] * dy + hull[2 * k + 1] * dx;
      if (a < minA) minA = a; if (a > maxA) maxA = a;
      if (b < minB) minB = b; if (b > maxB) maxB = b;
    }
    const ea = maxA - minA, eb = maxB - minB;
    const area = ea * eb;
    if (best === null || area < best.long * best.short - 1e-12) {
      const ca = (minA + maxA) / 2, cb = (minB + maxB) / 2;
      const cx = ca * dx - cb * dy, cy = ca * dy + cb * dx;
      best = ea >= eb
        ? { ax: dx, ay: dy, long: ea, short: eb, cx, cy }
        : { ax: -dy, ay: dx, long: eb, short: ea, cx, cy };
    }
  }
  return best!;
}

/**
 * Split a SIMPLE polygon by the line through (ox,oy) with unit direction
 * (dx,dy). Returns { left, right } piece lists (left = positive side of the
 * line normal (-dy,dx)), or null when the line GRAZES a vertex (non-general
 * position — the caller re-offsets and retries; a graze must never be
 * stitched, the crossing pairing would be wrong).
 */
export function splitPolygonByLine(
  poly: ArrayLike<number>, ox: number, oy: number, dx: number, dy: number,
): { left: number[][]; right: number[][] } | null {
  const n = poly.length / 2;
  const EPS = 1e-7;
  const side = new Float64Array(n); // signed distance to the line
  for (let i = 0; i < n; i++) {
    side[i] = -(poly[2 * i] - ox) * dy + (poly[2 * i + 1] - oy) * dx;
    if (Math.abs(side[i]) < EPS) return null; // graze — retry with a nudged cut
  }

  // Walk the boundary; emit points with crossings inserted. Each point carries
  // its side (+1/−1); crossings carry BOTH plus their t along the line.
  interface Pt { x: number; y: number; s: number; crossT: number | null; }
  const seq: Pt[] = [];
  let anyL = false, anyR = false;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const si = side[i] > 0 ? 1 : -1;
    if (si > 0) anyL = true; else anyR = true;
    seq.push({ x: poly[2 * i], y: poly[2 * i + 1], s: si, crossT: null });
    if (side[i] > 0 !== side[j] > 0) {
      const t = side[i] / (side[i] - side[j]);
      const cx = poly[2 * i] + (poly[2 * j] - poly[2 * i]) * t;
      const cy = poly[2 * i + 1] + (poly[2 * j + 1] - poly[2 * i + 1]) * t;
      seq.push({ x: cx, y: cy, s: 0, crossT: (cx - ox) * dx + (cy - oy) * dy });
    }
  }
  if (!anyL) return { left: [], right: [toPlain(poly)] };
  if (!anyR) return { left: [toPlain(poly)], right: [] };

  // Crossings sorted along the line pair up (0,1),(2,3),… — the inside spans.
  const crossings: number[] = [];
  for (let i = 0; i < seq.length; i++) if (seq[i].s === 0) crossings.push(i);
  if (crossings.length % 2 !== 0) return null; // defensive: should be impossible
  const order = crossings.slice().sort((a, b) => (seq[a].crossT! - seq[b].crossT!) || (a - b));
  const partner = new Map<number, number>();
  for (let k = 0; k + 1 < order.length; k += 2) {
    partner.set(order[k], order[k + 1]);
    partner.set(order[k + 1], order[k]);
  }

  // Arcs: maximal runs of same-side points bounded by crossings. Stitch cycles
  // via the bridge-partner permutation, per side.
  const m = seq.length;
  const nextIdx = (i: number): number => (i + 1) % m;
  const stitch = (want: number): number[][] => {
    const pieces: number[][] = [];
    const usedArcStart = new Set<number>(); // crossing index that STARTS a consumed arc
    for (let c0 = 0; c0 < m; c0++) {
      if (seq[c0].s !== 0 || usedArcStart.has(c0)) continue;
      // Does an arc of the wanted side start at this crossing?
      if (seq[nextIdx(c0)].s !== want) continue;
      const piece: number[] = [];
      let start = c0;
      let guard = 0;
      for (;;) {
        if (++guard > m + 4) return []; // defensive: corrupt stitch → no pieces
        usedArcStart.add(start);
        piece.push(seq[start].x, seq[start].y);
        let i = nextIdx(start);
        while (seq[i].s === want) { piece.push(seq[i].x, seq[i].y); i = nextIdx(i); }
        // i is the closing crossing of this arc; bridge to its partner, where
        // the next same-side arc begins.
        const p = partner.get(i);
        if (p === undefined) return [];
        piece.push(seq[i].x, seq[i].y);
        if (p === c0) break; // closed the cycle
        start = p;
      }
      if (piece.length >= 6) pieces.push(piece);
    }
    return pieces;
  };
  return { left: stitch(1), right: stitch(-1) };
}

function toPlain(poly: ArrayLike<number>): number[] {
  const out: number[] = new Array(poly.length);
  for (let i = 0; i < poly.length; i++) out[i] = poly[i];
  return out;
}

export interface SubdivideOpts {
  /** Split while a piece is bigger than this (m²). */
  maxLotArea: number;
  /** Drop pieces smaller than this (m²). */
  minLotArea: number;
  /** Split while the OBB aspect exceeds this. */
  aspectMax: number;
  /** Cut-point jitter fraction (± of half the long axis). */
  skew: number;
  /** Deterministic 0..1 draw keyed on the recursion path. */
  draw: (path: number) => number;
  /** Recursion depth cap (defensive). */
  depthCap?: number;
}

/**
 * Recursively subdivide a block polygon into lot polygons. Cuts run
 * perpendicular to the OBB long axis through mid ± skew·jitter; a graze
 * (vertex on the cut line) retries with a deterministic nudge. Pieces below
 * `minLotArea` are dropped; pieces within area + aspect bounds are lots.
 */
export function subdividePolygon(poly: number[], opts: SubdivideOpts): number[][] {
  const out: number[][] = [];
  const cap = opts.depthCap ?? 14;
  const rec = (p: number[], depth: number, path: number): void => {
    const area = Math.abs(polygonArea(p));
    if (area < opts.minLotArea) return;
    const obb = obbOf(p);
    const aspect = obb.short > 1e-6 ? obb.long / obb.short : Infinity;
    if ((area <= opts.maxLotArea && aspect <= opts.aspectMax) || depth >= cap) {
      if (area <= opts.maxLotArea * 1.5) out.push(p); // depth-capped oversize is dropped
      return;
    }
    // Cut perpendicular to the long axis at mid ± skew (jitter keyed on path).
    const jitter = (opts.draw(path) * 2 - 1) * opts.skew;
    for (let attempt = 0; attempt < 4; attempt++) {
      const off = (jitter + attempt * 0.093) * obb.long * 0.5;
      const ox = obb.cx + obb.ax * off;
      const oy = obb.cy + obb.ay * off;
      // The cut LINE runs across the polygon: direction = short axis.
      const split = splitPolygonByLine(p, ox, oy, -obb.ay, obb.ax);
      if (split === null) continue; // graze — nudge and retry
      if (split.left.length === 0 || split.right.length === 0) continue; // degenerate cut
      let child = 0;
      for (const piece of split.left) rec(piece, depth + 1, path * 8 + 1 + (child++ & 3));
      for (const piece of split.right) rec(piece, depth + 1, path * 8 + 5 + (child++ & 3));
      return;
    }
    // No valid cut found (heavily degenerate piece): accept if in-band, else drop.
    if (area <= opts.maxLotArea * 1.5) out.push(p);
  };
  rec(poly, 0, 1);
  return out;
}

/** Sutherland–Hodgman clip of a polygon against the half-plane keeping points
 *  with (p − o)·n ≥ 0 (n need not be unit). Returns [] when fully clipped. */
export function clipHalfPlane(poly: number[], ox: number, oy: number, nx: number, ny: number): number[] {
  const n = poly.length / 2;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const di = (poly[2 * i] - ox) * nx + (poly[2 * i + 1] - oy) * ny;
    const dj = (poly[2 * j] - ox) * nx + (poly[2 * j + 1] - oy) * ny;
    if (di >= 0) out.push(poly[2 * i], poly[2 * i + 1]);
    if (di > 0 !== dj > 0 && Math.abs(di - dj) > 1e-12) {
      const t = di / (di - dj);
      out.push(poly[2 * i] + (poly[2 * j] - poly[2 * i]) * t, poly[2 * i + 1] + (poly[2 * j + 1] - poly[2 * i + 1]) * t);
    }
  }
  return out.length >= 6 ? out : [];
}

/**
 * Total length of `poly`'s boundary lying within `band` metres of any segment
 * in `segs` ([ax,ay,bx,by, …] flat) — the "lot fronts a street" measure.
 * Sampled at ~1 m steps along the lot boundary (deterministic).
 */
export function sharedBoundaryLength(poly: number[], segs: ArrayLike<number>, band: number): number {
  const n = poly.length / 2;
  const nS = segs.length / 4;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = poly[2 * i], ay = poly[2 * i + 1];
    const bx = poly[2 * j], by = poly[2 * j + 1];
    const len = pmath.hypot(bx - ax, by - ay);
    const steps = Math.max(1, Math.ceil(len));
    const stepLen = len / steps;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const px = ax + (bx - ax) * t, py = ay + (by - ay) * t;
      let near = false;
      for (let k = 0; k < nS && !near; k++) {
        const sax = segs[4 * k], say = segs[4 * k + 1], sbx = segs[4 * k + 2], sby = segs[4 * k + 3];
        const dx = sbx - sax, dy = sby - say;
        const l2 = dx * dx + dy * dy;
        let u = l2 > 1e-12 ? ((px - sax) * dx + (py - say) * dy) / l2 : 0;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        const qx = sax + dx * u, qy = say + dy * u;
        near = (px - qx) * (px - qx) + (py - qy) * (py - qy) <= band * band;
      }
      if (near) total += s === 0 || s === steps ? stepLen * 0.5 : stepLen;
    }
  }
  return total;
}

/**
 * True iff segment (ax,ay)→(bx,by) properly intersects any boundary segment of
 * `poly` EXCLUDING segments incident to vertex indices `skipA`/`skipB` — the
 * alley-chord validity test (a chord may touch the boundary only at its own
 * endpoints).
 */
export function chordCrossesBoundary(
  poly: ArrayLike<number>, ax: number, ay: number, bx: number, by: number,
  skipA: number, skipB: number,
): boolean {
  const n = poly.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    if (i === skipA || j === skipA || i === skipB || j === skipB) continue;
    const cx = poly[2 * i], cy = poly[2 * i + 1];
    const dx2 = poly[2 * j], dy2 = poly[2 * j + 1];
    const d1 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    const d2 = (bx - ax) * (dy2 - ay) - (by - ay) * (dx2 - ax);
    const d3 = (dx2 - cx) * (ay - cy) - (dy2 - cy) * (ax - cx);
    const d4 = (dx2 - cx) * (by - cy) - (dy2 - cy) * (bx - cx);
    if (d1 * d2 < 0 && d3 * d4 < 0) return true;
  }
  return false;
}
